/**
 * Admin → Users (§27).
 *
 * Lists every Cognito user in the pool with a per-row admin toggle. Add or
 * remove the `admin` group via POST/DELETE /api/admin/users/{username}/admin.
 * The backend blocks self-demotion; the UI also disables the toggle on the
 * current operator's own row to surface this before the request is sent.
 */
import { useCallback, useEffect, useState } from "react";
import {
  addAdminUser,
  listAdminUsers,
  removeAdminUser,
} from "../services/admin.js";
import { getUserInfo } from "../services/auth.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingUsername, setTogglingUsername] = useState(null);

  const currentSub = getUserInfo()?.sub || null;

  const load = useCallback(() => {
    setLoading(true);
    return listAdminUsers()
      .then((data) => {
        setUsers(data.users || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleToggle(user, next) {
    const previous = users;
    setTogglingUsername(user.username);
    setUsers((list) =>
      list.map((u) =>
        u.username === user.username ? { ...u, isAdmin: next } : u,
      ),
    );
    try {
      if (next) {
        await addAdminUser(user.username);
      } else {
        await removeAdminUser(user.username);
      }
      setError(null);
    } catch (err) {
      setError(err.message);
      setUsers(previous);
    } finally {
      setTogglingUsername(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Users</h1>
      </div>
      <p className="admin-page__hint">
        Grant or revoke admin access. Users must sign up through the normal
        flow first — this panel only controls group membership, not user
        creation or deletion.
      </p>
      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : users.length === 0 ? (
        <p className="admin-page__empty">No users found.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Status</th>
              <th>Admin</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const isSelf = currentSub && currentSub === u.username;
              const disabled =
                togglingUsername === u.username || (isSelf && u.isAdmin);
              return (
                <tr key={u.username}>
                  <td>{u.email || u.username}</td>
                  <td>{u.name || "—"}</td>
                  <td>{u.status || "—"}</td>
                  <td>
                    <label className="admin-toggle">
                      <input
                        type="checkbox"
                        checked={Boolean(u.isAdmin)}
                        disabled={disabled}
                        onChange={(e) => handleToggle(u, e.target.checked)}
                        aria-label={`${u.isAdmin ? "Remove admin from" : "Grant admin to"} ${u.email || u.username}`}
                      />
                      <span>
                        {u.isAdmin ? "Admin" : "Member"}
                        {isSelf && u.isAdmin ? " (you)" : ""}
                      </span>
                    </label>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
